import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, clearSession, errMsg, downloadFile } from "@/lib/api";
import { TopBar } from "@/components/fly/Logo";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StartListTab } from "@/components/fly/StartListTab";
import { ResultsTab } from "@/components/fly/ResultsTab";
import { JudgesTab } from "@/components/fly/JudgesTab";
import { RegistrationBoard } from "@/components/fly/RegistrationBoard";
import { ResetScoresDialog } from "@/components/fly/ResetScoresDialog";
import { Download } from "lucide-react";
import { CardsTab } from "@/components/fly/CardsTab";
import { SignupDeadline } from "@/components/fly/SignupDeadline";

const downloadCsv = async (day) => {
  try {
    const slug = (day || "wszystkie-dni").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ł/g, "l").replace(/[^a-z0-9]+/g, "-");
    await downloadFile({ path: "/admin/results.csv", params: day ? { day } : {}, filename: `wyniki-${slug}.csv` });
  } catch (e) { toast.error(errMsg(e)); }
};

const DownloadPanel = () => {
  const [days, setDays] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/config").then((r) => setDays(r.data.days)); }, []);
  const btn = "h-11 px-3 rounded-xl border border-blue-500/40 text-blue-200 hover:bg-blue-500/10 text-sm flex items-center gap-2";
  return (
    <section className="fly-card p-5 mt-4 no-print" data-testid="download-panel">
      <button type="button" data-testid="download-toggle" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 text-left">
        <h2 className="font-display text-xl font-bold uppercase">Pobierz wyniki</h2>
        <span className="text-xs text-slate-400 flex items-center gap-1">{open ? "Zwiń" : "Rozwiń"}<ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {open && <>
      <p className="text-xs text-slate-400 mt-1">Plik CSV (Excel) ze wszystkimi wynikami: miejsca, oceny każdego sędziego, odjęcia i sumy.</p>
      <div className="flex flex-wrap gap-2 mt-3">
        <button data-testid="download-all-btn" onClick={() => downloadCsv(null)} className="h-11 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-sm font-semibold flex items-center gap-2"><Download className="w-4 h-4" />Wszystkie wyniki</button>
        {days.map((d) => (
          <button key={d} data-testid={`download-day-btn-${d.toLowerCase().replace(/[^a-z0-9ąćęłńóśźż]+/g, "-")}`} onClick={() => downloadCsv(d)} className={btn}><Download className="w-4 h-4" />{d}</button>
        ))}
      </div>
      </>}
    </section>
  );
};

const DayResetPanel = ({ onDone }) => {
  const [days, setDays] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/config").then((r) => setDays(r.data.days)); }, []);
  return (
    <section className="fly-card p-5 mt-4 no-print" data-testid="day-reset-panel">
      <button type="button" data-testid="day-reset-toggle" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 text-left">
        <h2 className="font-display text-xl font-bold uppercase text-red-300">Reset dni</h2>
        <span className="text-xs text-slate-400 flex items-center gap-1">{open ? "Zwiń" : "Rozwiń"}<ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {open && <>
      <p className="text-xs text-slate-400 mt-1">Wymazuje oceny oraz rejestrację (obecność i ubezpieczenia) tylko z wybranego dnia i ustawia konferansjera na pierwszy występ. Wymaga hasła administratora; przed resetem zapisuje się i pobiera kopia wyników w Excelu.</p>
      <div className="flex flex-wrap gap-2 mt-3">
        {days.map((d) => (
          <ResetScoresDialog key={d} days={days} fixedDay={d} label={`Reset: ${d}`} onDone={onDone}
            testid={`day-reset-btn-${d.toLowerCase().replace(/[^a-z0-9ąćęłńóśźż]+/g, "-")}`} />
        ))}
      </div>
      </>}
    </section>
  );
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [ov, setOv] = useState(null);
  const [rk, setRk] = useState(0);

  const load = useCallback(() => {
    api.get("/admin/overview").then((r) => setOv(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setSetting = async (patch, msg) => {
    try {
      await api.put("/admin/settings", patch);
      toast.success(msg);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const togglePublic = (field, v) => setSetting({ [field]: v }, v ? "Widoczne publicznie" : "Ukryte dla publiczności");
  const toggleBlock = (v) => setSetting({ announcer_block: v }, v ? "Blokada konferansjera włączona" : "Blokada konferansjera wyłączona – może przechodzić dalej bez kompletu ocen");

  return (
    <>
      <TopBar title="Organizator" back="/organizacja" right={
        <>
          <button data-testid="admin-logout-btn" onClick={() => { clearSession(); nav("/admin/login"); }} className="h-10 px-3 rounded-lg border border-white/10 text-slate-300 hover:text-white flex items-center gap-2 text-sm"><LogOut className="w-4 h-4" /></button>
        </>
      } />
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-6">
        {ov && <Overview ov={ov} onToggle={togglePublic} onPpvUrl={(v) => setSetting({ ppv_url: v }, v ? "Zapisano link PPV" : "Usunięto link PPV")} onToggleBlock={toggleBlock} onDeadline={(v) => setSetting({ signup_deadline: v }, v ? "Zapisano termin zakończenia zapisów" : "Usunięto termin zakończenia zapisów")} onStart={(v) => setSetting({ signup_start: v }, v ? "Zapisano datę rozpoczęcia zapisów" : "Usunięto datę rozpoczęcia zapisów")} />}
        <DownloadPanel />
        <DayResetPanel onDone={() => { load(); setRk((x) => x + 1); }} />
        <Tabs defaultValue="start" className="mt-8">
          <TabsList className="no-print bg-[#1C2541] h-auto sm:h-12 p-1 w-full grid grid-cols-2 sm:flex gap-1">
            <TabsTrigger data-testid="admin-tab-start" value="start" className="h-10 px-5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Lista startowa</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-results" value="results" className="h-10 px-5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Wyniki</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-registration" value="registration" className="h-10 px-5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Rejestracja</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-judges" value="judges" className="h-10 px-5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Użytkownicy</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-cards" value="cards" className="h-10 px-5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Ocenianie</TabsTrigger>
          </TabsList>
          <TabsContent value="start"><StartListTab key={`s${rk}`} onChange={load} /></TabsContent>
          <TabsContent value="results"><ResultsTab key={`r${rk}`} onChange={load} /></TabsContent>
          <TabsContent value="registration"><RegistrationBoard /></TabsContent>
          <TabsContent value="judges"><JudgesTab onChange={load} /></TabsContent>
          <TabsContent value="cards"><CardsTab /></TabsContent>
        </Tabs>
      </main>
    </>
  );
}

const VIS = [
  ["ppv_public", "PPV – oglądaj na żywo", "admin-ppv-public-switch"],
  ["now_public", "Teraz na scenie", "admin-now-public-switch"],
  ["results_public", "Wyniki na żywo", "admin-public-switch"],
  ["startlist_public", "Lista startowa", "admin-startlist-public-switch"],
  ["signup_open", "Zapisy (formularz zgłoszeń)", "admin-signup-switch"],
];

const signupStatus = (s) => {
  if (!s.signup_open) return "Zamknięte";
  if (s.signup_start && new Date(s.signup_start) > new Date()) return "Zamknięte (przed rozpoczęciem)";
  if (s.signup_deadline && new Date(s.signup_deadline) < new Date()) return "Zamknięte (termin minął)";
  return "Otwarte";
};

const PpvUrl = ({ value, onSave }) => {
  const [v, setV] = useState(value || "");
  useEffect(() => { setV(value || ""); }, [value]);
  return (
    <div className="flex gap-2" data-testid="admin-ppv-url">
      <input data-testid="admin-ppv-url-input" value={v} onChange={(e) => setV(e.target.value)} placeholder="Link PPV: https://…"
        className="flex-1 min-w-0 h-9 px-2 rounded-lg bg-[#0B132B] border border-blue-500/20 text-sm" />
      <button data-testid="admin-ppv-url-save" onClick={() => onSave(v.trim())} className="h-9 px-3 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold">Zapisz</button>
    </div>
  );
};

const Overview = ({ ov, onToggle, onPpvUrl, onToggleBlock, onDeadline, onStart }) => {
  const pct = ov.performances ? Math.round((ov.complete / ov.performances) * 100) : 0;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 no-print">
      <div className="fly-card p-5 col-span-2 lg:col-span-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400">Komplet ocen</div>
        <div data-testid="overview-complete" className="font-display text-5xl font-black mt-1">{ov.complete}<span className="text-slate-500 text-2xl"> / {ov.performances}</span></div>
        <div className="h-1.5 rounded-full bg-white/5 mt-3 overflow-hidden"><div className="h-full bg-gradient-to-r from-blue-600 to-cyan-400" style={{ width: `${pct}%` }} /></div>
        <div className="text-xs text-slate-400 mt-2">{ov.categories} kategorii</div>
      </div>
      {Object.entries(ov.per_judge).map(([n, c]) => (
        <div key={n} className="fly-card p-5" data-testid={`overview-judge-${n}`}>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Sędzia {n}</div>
          <div className="font-display text-4xl font-bold mt-1">{c}</div>
          <div className="text-xs text-slate-500">ocenionych</div>
        </div>
      ))}
      <div className="fly-card p-5 col-span-2 flex flex-col gap-2" data-testid="admin-visibility-card">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Widok publiczny (strona główna)</div>
        {VIS.map(([field, label, tid]) => (
          <div key={field} className="grid gap-2">
          <label className="flex items-center gap-3 cursor-pointer text-sm">
            <Switch data-testid={tid} checked={!!ov.settings[field]} onCheckedChange={(v) => onToggle(field, v)} />
            <span className="flex-1">{label}</span>
            <span className={ov.settings[field] ? "text-emerald-400 font-semibold" : "text-slate-500"}>{field === "signup_open" ? signupStatus(ov.settings) : (ov.settings[field] ? "Publiczne" : "Ukryte")}</span>
          </label>
          {field === "ppv_public" && <PpvUrl value={ov.settings.ppv_url} onSave={onPpvUrl} />}
          </div>
        ))}
        <SignupDeadline value={ov.settings.signup_start} onSave={onStart} label="Rozpoczęcie zapisów" tid="admin-signup-start" pastText="rozpoczęte" pastClass="text-emerald-400" />
        <SignupDeadline value={ov.settings.signup_deadline} onSave={onDeadline} />
      </div>
      <div className="fly-card p-5 col-span-2 lg:col-span-1 flex flex-col justify-between" data-testid="admin-block-card">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Blokada konferansjera</div>
        <label className="flex items-center gap-3 mt-3 cursor-pointer">
          <Switch data-testid="admin-block-switch" checked={ov.settings.announcer_block} onCheckedChange={onToggleBlock} />
          <span data-testid="admin-block-status" className={`text-sm ${ov.settings.announcer_block ? "text-emerald-400 font-semibold" : "text-amber-400 font-semibold"}`}>{ov.settings.announcer_block ? "Włączona" : "Wyłączona"}</span>
        </label>
        <p className="text-[11px] text-slate-500 mt-2">Gdy włączona, konferansjer czeka na komplet ocen.</p>
      </div>
    </div>
  );
};
